import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { paths } from '@shared/config';

const KNOWN_CODES = [
  'OAUTH_PROVIDER_ERROR',
  'OAUTH_MISSING_PARAMS',
  'INVALID_OAUTH_STATE',
  'OAUTH_PROVIDER_MISMATCH',
  'OAUTH_LOGIN_FAILED',
  'OAUTH_REFRESH_FAILED',
] as const;

type KnownCode = (typeof KNOWN_CODES)[number];

const isKnownCode = (value: string | null): value is KnownCode =>
  !!value && (KNOWN_CODES as readonly string[]).includes(value);

export function OAuthErrorContent() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const rawCode = searchParams.get('code');
  const message = searchParams.get('message');
  const codeKey: KnownCode | 'generic' = isKnownCode(rawCode) ? rawCode : 'generic';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32 }}
      className="flex max-w-[400px] flex-col items-center gap-[18px] text-center"
    >
      <motion.div
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: 'spring', stiffness: 240, damping: 16 }}
        className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-danger/15 text-3xl text-danger"
      >
        ✕
      </motion.div>

      <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
        {t('auth.oauth.error.title')}
      </h1>
      <div className="text-[14px] leading-[1.6] text-muted">{t(`auth.oauth.error.${codeKey}`)}</div>

      {message && (
        <div className="w-full rounded-xl border border-line bg-card px-3.5 py-2.5 text-left font-mono text-[12px] leading-[1.5] text-muted-2">
          {message}
        </div>
      )}

      {rawCode && (
        <div className="rounded-full border border-line bg-card px-2.5 py-[3px] font-mono text-[12px] tracking-[0.06em] text-muted-2">
          {rawCode}
        </div>
      )}

      <Link
        to={paths.auth}
        className="mt-1 inline-flex items-center justify-center gap-2 rounded-pill bg-accent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg-inv transition-colors hover:bg-accent-hi"
      >
        <span>{t('auth.oauth.error.back')}</span>
        <span aria-hidden className="text-sm">
          →
        </span>
      </Link>
    </motion.div>
  );
}
